import { useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import { useLocationFishGroupsController_create } from '@/services/hooks';
import type { CreateFishGroupRequest } from '@/services/types';

import { invalidateFishGroupQueries } from './_invalidateQueries';

/** A customer creating a group in an empty pen of a site, inside one of its stocking cycles. */
export interface CreateSiteFishGroupInput {
    stockingCycleId: number;
    cageId: number;
    name: string;
    fishTypeId: number;
    /** ISO timestamp; see `toOperationTimestamp`. */
    startDate: string;
    initialPopulationSize?: number;
    reason?: string;
    notes?: string;
}

export function mapCreateSiteFishGroupToRequest(
    input: CreateSiteFishGroupInput
): CreateFishGroupRequest {
    const reason = input.reason?.trim();
    const notes = input.notes?.trim();
    return {
        stockingCycleId: input.stockingCycleId,
        cageId: input.cageId,
        name: input.name.trim(),
        fishTypeId: input.fishTypeId,
        startDate: input.startDate,
        ...(input.initialPopulationSize !== undefined
            ? { initialPopulationSize: input.initialPopulationSize }
            : {}),
        ...(reason ? { reason } : {}),
        ...(notes ? { notes } : {}),
    };
}

/**
 * The location-scoped create (`POST fish-groups/:clientId/locations/:locationId/fish-groups`),
 * which admits site managers, unlike the internal `:clientId/create` route.
 */
export interface UseCreateSiteFishGroupResult {
    /** Resolves once the board's queries have refetched; rejects with the request error. */
    create: (input: CreateSiteFishGroupInput) => Promise<void>;
    isLoading: boolean;
    error: Error | RequestError | null;
    reset: () => void;
}

export const useCreateSiteFishGroup = (
    clientId: number,
    locationId: number
): UseCreateSiteFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useLocationFishGroupsController_create();

    const create = async (input: CreateSiteFishGroupInput): Promise<void> => {
        await mutation.mutateAsync({
            clientId,
            locationId,
            requestBody: mapCreateSiteFishGroupToRequest(input),
        });
        // Covers this site's board too, with the client's other fish group reads.
        await invalidateFishGroupQueries(queryClient, clientId);
    };

    return {
        create,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
